import React, { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Select } from '@crabs-ai/web-icons';
import { SettingPanel } from '@/features/brand-template/ui/panels';
import { enterGroupMember } from '../state/actions/select-group';
import { setActiveSelectionSurface } from '../state/actions/set-active-selection-surface';
import { useItems, useWriteContext } from '../utils/use-context';
import type { EditorStarterItem } from '../items/item-type';

/** 멤버 행 라벨 — text 는 내용 일부, 그 외는 type 이름을 보여준다. */
const getMemberLabel = (item: EditorStarterItem): string => {
  if (item.type === 'text') {
    const text = item.text?.trim();
    if (text) return text.slice(0, 30);
  }
  return item.type;
};

/**
 * 그룹 멤버 행 목록 — 멤버를 클릭하면 onSelectMember 로 선택을 위임한다.
 * 구세대 그룹(GroupInspector, enterGroupMember dispatch)과
 * 1급 GroupItem(GroupItemInspector, 로컬 state)이 같은 리스트 UI 를 공유한다.
 */
export const GroupMemberList: React.FC<{
  memberIds: string[];
  onSelectMember: (memberId: string) => void;
}> = ({ memberIds, onSelectMember }) => {
  const items = useItems(memberIds);

  return (
    <ul className="flex flex-col gap-4xs px-sm py-2xs">
      {items.map((item, index) => (
        <li key={item.id}>
          <button
            type="button"
            onClick={() => onSelectMember(item.id)}
            className="flex w-full items-center gap-2xs rounded-2xs px-2xs py-3xs text-left transition-colors hover:bg-sys-bg-surface-hover focus-visible:ring-2 focus-visible:ring-sys-style-primary-default focus-visible:outline-none"
          >
            <span className="w-4 shrink-0 text-caption-small text-sys-cont-neutral-lightest">
              {index + 1}
            </span>
            <span className="truncate text-body-small text-sys-cont-neutral-default">
              {getMemberLabel(item)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
};

/**
 * 그룹을 한 덩어리로 선택했을 때 inspector 에 표시하는 멤버 목록.
 * 멤버를 클릭하면 그 멤버로 depth 진입(enterGroupMember)하여 canvas 선택과 연동된다
 * (canvas 더블클릭 depth 와 동일한 액션). 선택 전환이라 undo 스택에는 쌓지 않는다.
 */
export const GroupInspector: React.FC<{ memberIds: string[] }> = ({
  memberIds,
}) => {
  const t = useTranslations();
  const items = useItems(memberIds);
  const { setState } = useWriteContext();
  const groupId = items[0]?.groupId ?? null;

  const handleSelectMember = useCallback(
    (memberId: string) => {
      if (groupId == null) return;
      setState({
        update: (state) =>
          setActiveSelectionSurface(
            enterGroupMember(state, groupId, memberId),
            'canvas',
          ),
        commitToUndoStack: false,
      });
    },
    [groupId, setState],
  );

  return (
    <SettingPanel
      icon={<Select />}
      title={t('Editor.inspector.group.title', { count: items.length })}
    >
      <GroupMemberList
        memberIds={memberIds}
        onSelectMember={handleSelectMember}
      />
    </SettingPanel>
  );
};
